import React from "react";
import { ArrowRight, CheckCircle } from "lucide-react";
import DashboardImage from "../../assets/images/dashboard-hero.png";
import { useLanguage } from "../../context/LanguageProvider";

const HeroSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-[90vh] flex items-center pt-28 sm:pt-36 lg:pt-32 pb-16 lg:pb-24 overflow-hidden bg-slate-900 w-full">
      {/* Background Gradients (contained to avoid horizontal overflow) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[350px] sm:w-[600px] lg:w-[800px] h-[350px] sm:h-[600px] lg:h-[800px] bg-primary/20 rounded-full blur-[100px] sm:blur-[120px] mix-blend-screen animate-pulse duration-[10s]"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[300px] sm:w-[500px] lg:w-[600px] h-[300px] sm:h-[500px] lg:h-[600px] bg-secondary/20 rounded-full blur-[80px] sm:blur-[100px] mix-blend-screen"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          
          {/* Text Content */}
          <div className="w-full lg:w-1/2 space-y-6 sm:space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 backdrop-blur-md">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-300 text-xs sm:text-sm font-bold tracking-wide">
                {t("hero_storefront_badge") || "🛍️ Boutique en Ligne WhatsApp (100% Gratuit à Vie)"}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight">
              {t("hero_title_part1")} <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">
                {t("hero_title_part2")}
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-slate-300 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light">
              {t("hero_description")}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 justify-center lg:justify-start pt-2">
              <a
                href="https://app.fukkoni.com/register"
                className="w-full sm:w-auto px-7 py-4 bg-primary hover:bg-primary-dark text-white rounded-2xl font-bold flex items-center justify-center transition-all shadow-premium hover:shadow-emerald-500/20 active:scale-98 text-sm sm:text-base"
              >
                {t("hero_button_getStarted")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>

              <a
                href="https://app.fukkoni.com/register?plan=lite"
                className="w-full sm:w-auto px-7 py-4 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:text-white rounded-2xl font-bold flex items-center justify-center backdrop-blur-sm transition-all hover:border-emerald-400 active:scale-98 text-sm sm:text-base"
              >
                {t("hero_button_create_store") || "Créer un Lien Gratuit (Gratuit à Vie)"}
              </a>
            </div>

            <div className="pt-6 sm:pt-8 border-t border-white/10">
              <p className="text-xs sm:text-sm text-slate-400 mb-3 font-semibold uppercase tracking-wider">{t("hero_trustedBy")}</p>
              <div className="flex items-center justify-center lg:justify-start gap-4 sm:gap-8 opacity-60 grayscale hover:grayscale-0 transition-all duration-300 flex-wrap">
                <div className="h-7 w-20 sm:h-8 sm:w-24 bg-white/15 rounded-lg"></div>
                <div className="h-7 w-20 sm:h-8 sm:w-24 bg-white/15 rounded-lg"></div>
                <div className="h-7 w-20 sm:h-8 sm:w-24 bg-white/15 rounded-lg"></div>
                <div className="h-7 w-20 sm:h-8 sm:w-24 bg-white/15 rounded-lg"></div>
              </div>
            </div>
          </div>

          {/* Hero Image / Dashboard Mockup */}
          <div className="w-full lg:w-1/2 relative flex items-center justify-center">
            <div className="relative w-full max-w-lg lg:max-w-none rounded-2xl bg-slate-800/60 border border-white/10 p-1.5 sm:p-2.5 shadow-2xl backdrop-blur-xl transition-all duration-500">
              <img 
                src={DashboardImage} 
                alt="Fukkoni Dashboard" 
                className="rounded-xl w-full h-auto shadow-inner object-cover"
              />
              
              {/* Floating Cards (hidden on very small screens, visible on md+) */}
              <div className="absolute -bottom-6 -left-6 bg-white/95 backdrop-blur-md p-3.5 rounded-2xl shadow-enterprise hidden sm:block border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-100 rounded-xl">
                     <CheckCircle className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{t("hero_stats_monthlySales")}</p>
                    <p className="text-lg font-black text-slate-900">+124%</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decoration Glow behind image */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-radial from-primary/15 to-transparent blur-2xl pointer-events-none"></div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
